import React, { useCallback, useContext, useRef, useState } from "react";
import { Box, Button, Flex, Input, Badge, Text } from "@chakra-ui/react";
import { Switch } from "@/components/ui/switch";
import { SPACING } from "@/generic/config";
import { Context } from "@/App";
import { toaster } from "@/components/ui/toaster";
import * as XLSX from "xlsx";
import Tooltip from "@/base-components/Tooltip";

export type ImportColumnType = "string" | "boolean" | "number";

export type ImportColumn<T> = {
    header: string;
    fieldKey: keyof T;
    type: ImportColumnType;
};

type ImportStatus = "pending" | "imported" | "failed";

export type ImportRow<T> = {
    data: Record<string, any>;
    status: ImportStatus;
    error?: string;
};

type ImportTableProps<T> = {
    columns: ImportColumn<T>[];
    createFn: (data: any) => Promise<number>;
    buildPayload: (rowData: Record<string, any>) => any;
};

const INITIAL_ROW_COUNT = 10;
const ADD_ROW_COUNT = 10;

function createEmptyRows<T>(count: number, columns: ImportColumn<T>[]): ImportRow<T>[] {
    return Array.from({ length: count }, () => ({
        data: Object.fromEntries(columns.map(col => [col.fieldKey as string, col.type === "boolean" ? false : col.type === "number" ? "" : ""])),
        status: "pending" as ImportStatus,
    }));
}

function isRowEmpty<T>(row: ImportRow<T>, columns: ImportColumn<T>[]): boolean {
    return columns.every(col => {
        const val = row.data[col.fieldKey as string];
        if (col.type === "boolean") return val === false;
        if (col.type === "number") return val === "" || val === 0;
        return !val || val === "";
    });
}

export default function ImportTable<T>({ columns, createFn, buildPayload }: ImportTableProps<T>) {
    const { translations } = useContext(Context);
    const t = translations.importExport;
    const [rows, setRows] = useState<ImportRow<T>[]>(() => createEmptyRows(INITIAL_ROW_COUNT, columns));
    const [importingAll, setImportingAll] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const updateRow = useCallback((index: number, updater: (row: ImportRow<T>) => ImportRow<T>) => {
        setRows(prev => prev.map((r, i) => i === index ? updater(r) : r));
    }, []);

    const handleCellChange = useCallback((rowIndex: number, fieldKey: string, value: any) => {
        updateRow(rowIndex, row => ({
            ...row,
            data: { ...row.data, [fieldKey]: value },
        }));
    }, [updateRow]);

    const importSingleRow = useCallback(async (index: number) => {
        const row = rows[index];
        if (row.status === "imported") return;

        updateRow(index, r => ({ ...r, status: "pending", error: undefined }));

        try {
            const payload = buildPayload(row.data);
            await createFn(payload);
            updateRow(index, r => ({ ...r, status: "imported", error: undefined }));
        } catch (err: any) {
            const message = err?.response?.data?.message || err?.message || "Unknown error";
            updateRow(index, r => ({ ...r, status: "failed", error: message }));
        }
    }, [rows, createFn, buildPayload, updateRow]);

    const importAllRows = useCallback(async () => {
        setImportingAll(true);
        // Get the current rows snapshot
        const currentRows = rows;
        for (let i = 0; i < currentRows.length; i++) {
            if (currentRows[i].status === "imported") continue;
            if (isRowEmpty(currentRows[i], columns)) continue;

            updateRow(i, r => ({ ...r, error: undefined }));

            try {
                const payload = buildPayload(currentRows[i].data);
                await createFn(payload);
                updateRow(i, r => ({ ...r, status: "imported", error: undefined }));
            } catch (err: any) {
                const message = err?.response?.data?.message || err?.message || "Unknown error";
                updateRow(i, r => ({ ...r, status: "failed", error: message }));
            }
        }
        setImportingAll(false);
    }, [rows, columns, createFn, buildPayload, updateRow]);

    const addMoreRows = useCallback(() => {
        setRows(prev => [...prev, ...createEmptyRows(ADD_ROW_COUNT, columns)]);
    }, [columns]);

    const copyTemplate = useCallback(() => {
        const headerText = columns.map(c => c.header).join("\t");
        navigator.clipboard.writeText(headerText);
        toaster.create({ title: t.templateCopied, type: "success" });
    }, [columns, t.templateCopied]);

    const handleFileDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        const file = e.dataTransfer.files[0];
        if (file) processFile(file);
    }, []);

    const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) processFile(file);
        // Reset so re-selecting the same file works
        e.target.value = "";
    }, []);

    const processFile = useCallback((file: File) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            const data = new Uint8Array(e.target?.result as ArrayBuffer);
            const workbook = XLSX.read(data, { type: "array" });
            const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
            const jsonData = XLSX.utils.sheet_to_json<Record<string, any>>(firstSheet);

            if (jsonData.length === 0) {
                toaster.create({ title: t.noDataInFile, type: "warning" });
                return;
            }

            // Build a header-to-fieldKey map
            const headerToField = new Map<string, { fieldKey: string; type: ImportColumnType }>();
            columns.forEach(col => {
                headerToField.set(col.header, { fieldKey: col.fieldKey as string, type: col.type });
            });

            const newRows: ImportRow<T>[] = jsonData.map(excelRow => {
                const rowData: Record<string, any> = Object.fromEntries(
                    columns.map(col => [col.fieldKey as string, col.type === "boolean" ? false : col.type === "number" ? "" : ""])
                );

                Object.entries(excelRow).forEach(([key, value]) => {
                    const match = headerToField.get(key);
                    if (match) {
                        if (match.type === "boolean") {
                            rowData[match.fieldKey] = value === true || value === "true" || value === "1" || value === "yes";
                        } else if (match.type === "number") {
                            rowData[match.fieldKey] = value === "" || value == null ? "" : Number(value);
                        } else {
                            rowData[match.fieldKey] = String(value ?? "");
                        }
                    }
                });

                return { data: rowData, status: "pending" as ImportStatus };
            });

            setRows(newRows);
            toaster.create({ title: `${newRows.length} ${t.rowsAdded}`, type: "success" });
        };
        reader.readAsArrayBuffer(file);
    }, [columns, t]);

    const parseTabSeparatedRows = useCallback((text: string): ImportRow<T>[] => {
        const lines = text.split("\n").filter(line => line.trim() !== "");
        if (lines.length < 2) return [];

        const headers = lines[0].split("\t").map(h => h.trim());

        const headerToField = new Map<string, { fieldKey: string; type: ImportColumnType }>();
        columns.forEach(col => {
            headerToField.set(col.header, { fieldKey: col.fieldKey as string, type: col.type });
        });

        return lines.slice(1).map(line => {
            const values = line.split("\t");
            const rowData: Record<string, any> = Object.fromEntries(
                columns.map(col => [col.fieldKey as string, col.type === "boolean" ? false : col.type === "number" ? "" : ""])
            );

            headers.forEach((header, i) => {
                const match = headerToField.get(header);
                if (match && i < values.length) {
                    const value = values[i]?.trim();
                    if (match.type === "boolean") {
                        rowData[match.fieldKey] = value === "true" || value === "1" || value === "yes" || value === "TRUE";
                    } else if (match.type === "number") {
                        rowData[match.fieldKey] = value === "" ? "" : Number(value);
                    } else {
                        rowData[match.fieldKey] = value ?? "";
                    }
                }
            });

            return { data: rowData, status: "pending" as ImportStatus };
        });
    }, [columns]);

    const createFromClipboard = useCallback(async () => {
        try {
            const text = await navigator.clipboard.readText();
            const newRows = parseTabSeparatedRows(text);
            if (newRows.length === 0) {
                toaster.create({ title: t.noDataInFile, type: "warning" });
                return;
            }
            setRows(newRows);
            toaster.create({ title: `${newRows.length} ${t.rowsAdded}`, type: "success" });
        } catch {
            toaster.create({ title: "Could not read clipboard", type: "error" });
        }
    }, [parseTabSeparatedRows, t]);

    const statusBadge = (status: ImportStatus, error?: string) => {
        const colorMap: Record<ImportStatus, string> = {
            pending: "gray",
            imported: "green",
            failed: "red",
        };
        const labelMap: Record<ImportStatus, string> = {
            pending: t.notYetImported,
            imported: t.imported,
            failed: t.failed,
        };
        return (
            <Box>
                <Tooltip tooltip={<span style={{ fontSize: 14 }}>{error ?? labelMap[status]}</span>}>
                    <Badge colorPalette={colorMap[status]} variant="solid" fontSize="xs">
                        {labelMap[status]}
                    </Badge>
                </Tooltip>
            </Box>
        );
    };

    const MIN_COL_WIDTH = "140px";

    return (
        <Box>
            {/* Top toolbar */}
            <Flex gap={SPACING} mb={SPACING} flexWrap="wrap" alignItems="center">
                <Button size="sm" variant="outline" onClick={createFromClipboard}>
                    {t.createFromClipboard}
                </Button>
                <Button size="sm" bg="red" onClick={importAllRows} disabled={importingAll}>
                    {importingAll ? t.importing : t.importAll}
                </Button>
                <Button size="sm" variant="outline" onClick={copyTemplate}>
                    {t.copyTemplate}
                </Button>
            </Flex>

            {/* Excel drop zone */}
            <Box
                border="2px dashed"
                borderColor="gray.300"
                borderRadius="md"
                p={4}
                mb={SPACING}
                textAlign="center"
                cursor="pointer"
                onDragOver={(e) => e.preventDefault()}
                onDrop={handleFileDrop}
                onClick={() => fileInputRef.current?.click()}
                _hover={{ borderColor: "blue.400", bg: "blue.50" }}
            >
                <Text fontSize="sm" color="gray.500">{t.excelImport}</Text>
                <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    style={{ display: "none" }}
                    onChange={handleFileSelect}
                />
            </Box>

            {/* Table */}
            <Box overflowX="auto">
                <Box as="table" width="100%" fontSize="sm" borderCollapse="collapse">
                    <Box as="thead">
                        <Box as="tr">
                            <Box as="th" p={2} textAlign="left" minW="120px" borderBottom="2px solid" borderColor="gray.200">
                                {t.status}
                            </Box>
                            <Box as="th" p={2} textAlign="left" minW="80px" borderBottom="2px solid" borderColor="gray.200">
                                {t.import}
                            </Box>
                            {columns.map(col => (
                                <Box as="th" key={col.fieldKey as string} p={2} textAlign="left" minW={MIN_COL_WIDTH} borderBottom="2px solid" borderColor="gray.200">
                                    {col.header}
                                </Box>
                            ))}
                        </Box>
                    </Box>
                    <Box as="tbody">
                        {rows.map((row, rowIndex) => {
                            const isImported = row.status === "imported";
                            return (
                                <Box as="tr" key={rowIndex} bg={isImported ? "green.50" : undefined} _dark={isImported ? { bg: "green.900" } : undefined}>
                                    <Box as="td" p={2} borderBottom="1px solid" borderColor="gray.100">
                                        {statusBadge(row.status, row.error)}
                                    </Box>
                                    <Box as="td" p={2} borderBottom="1px solid" borderColor="gray.100">
                                        <Button
                                            size="xs"
                                            disabled={isImported || importingAll}
                                            onClick={() => importSingleRow(rowIndex)}
                                        >
                                            {t.import}
                                        </Button>
                                    </Box>
                                    {columns.map(col => {
                                        const fieldKey = col.fieldKey as string;
                                        const value = row.data[fieldKey];
                                        return (
                                            <Box as="td" key={fieldKey} p={1} borderBottom="1px solid" borderColor="gray.100" minW={MIN_COL_WIDTH}>
                                                {col.type === "boolean" ? (
                                                    <Switch
                                                        size="sm"
                                                        defaultChecked={!!value}
                                                        disabled={isImported}
                                                        onChange={() => handleCellChange(rowIndex, fieldKey, !value)}
                                                    />
                                                ) : (
                                                    <Input
                                                        size="sm"
                                                        type={col.type === "number" ? "number" : "text"}
                                                        value={value ?? ""}
                                                        disabled={isImported}
                                                        onChange={(e) => {
                                                            const v = col.type === "number" && e.target.value !== ""
                                                                ? Number(e.target.value)
                                                                : e.target.value;
                                                            handleCellChange(rowIndex, fieldKey, v);
                                                        }}
                                                    />
                                                )}
                                            </Box>
                                        );
                                    })}
                                </Box>
                            );
                        })}
                    </Box>
                </Box>
            </Box>

            {/* Add more rows button */}
            <Flex justifyContent="center" mt={SPACING}>
                <Button size="sm" variant="outline" onClick={addMoreRows}>
                    {t.addRows}
                </Button>
            </Flex>
        </Box>
    );
}
